import axios from 'axios';
import { errorInterceptor } from './errorInterceptor';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

let tokenRefreshPromise: Promise<TokenResponse> | null = null;
let lastTokenRefreshTime = 0;
let pendingRefresh = false;

const saveTokens = (accessToken: string, refreshToken: string): void => {
  const updatedAuthState = {
    accessToken,
    refreshToken,
  };
  localStorage.setItem('authState', JSON.stringify(updatedAuthState));
  localStorage.setItem('lastTokenRefresh', Date.now().toString());
  lastTokenRefreshTime = Date.now();

  try {
    const base64Url = accessToken.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    if (payload.exp) {
      const expirationTime = payload.exp * 1000;
      localStorage.setItem('tokenExpiresAt', expirationTime.toString());
    }
  } catch (error) {
    void error;
  }
};

// Token yenileme işlemi
const refreshTokenAsync = async (refreshToken: string): Promise<TokenResponse> => {
  // Eğer aktif bir yenileme işlemi varsa, onu kullan
  if (pendingRefresh && tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  // Yenileme işleminin başladığını işaretle
  pendingRefresh = true;

  // Yeni token al
  const promise = axios({
    method: 'post',
    url: `${import.meta.env.VITE_AUTH_TOKEN_URL}`,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: 'true',
      Accept: 'application/json, text/plain, */*',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
    data: `grant_type=refresh_token&client_id=merzigogateway&client_secret=merzigogateway_secret&refresh_token=${refreshToken}`,
  })
    .then((response) => {
      const { access_token, refresh_token } = response.data;

      // Yeni token bilgilerini kaydet
      saveTokens(access_token, refresh_token);

      return response.data as TokenResponse;
    })
    .catch((error) => {
      void error;
      throw error;
    })
    .finally(() => {
      // İşlem tamamlandığında flag'i sıfırla
      setTimeout(() => {
        pendingRefresh = false;
        tokenRefreshPromise = null;
      }, 100);
    });

  tokenRefreshPromise = promise;
  return promise;
};

// Token süresi kontrolü - doğrudan localStorage'dan da kontrol eklenmiş
const checkTokenStatus = (token: string): 'valid' | 'needs_refresh' | 'expired' => {
  if (!token) {
    return 'expired';
  }

  try {
    const currentTime = Date.now();

    // Token'dan süre bilgisini çıkar
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));

    if (!payload.exp) {
      return 'valid';
    }

    const expirationTime = payload.exp * 1000;
    const remainingTime = expirationTime - currentTime;

    // Süreyi localStorage'a kaydedelim
    localStorage.setItem('tokenExpiresAt', expirationTime.toString());

    // Kalan süreyi logla

    // Proaktif yenileme için tampon süre (5 dakika)
    const bufferTime = 5 * 60 * 1000;

    // Süresi dolduysa
    if (remainingTime <= 0) {
      return 'expired';
    }

    if (remainingTime <= bufferTime) {
      const timeSinceLastRefresh = currentTime - lastTokenRefreshTime;

      if (timeSinceLastRefresh < 10 * 1000) {
        return 'valid';
      }

      return 'needs_refresh';
    }

    return 'valid';
  } catch (error) {
    void error;
    return 'expired';
  }
};

const createApiClient = (baseURL: string) => {
  const isAuthTokenClient = baseURL === import.meta.env.VITE_AUTH_TOKEN_URL;
  const apiClient = axios.create({
    baseURL,
    headers: {
      'Cache-Control': undefined,
    },
  });

  apiClient.interceptors.request.use(
    async (config) => {
      // Her istekte Accept-Language header'ını ekle (refresh token istekleri hariç)
      const isRefreshTokenRequest =
        config.url === import.meta.env.VITE_AUTH_TOKEN_URL &&
        config.data &&
        typeof config.data === 'string' &&
        config.data.includes('grant_type=refresh_token');

      if (!isRefreshTokenRequest) {
        config.headers['Accept-Language'] =
          localStorage.getItem('i18nextLng') === 'tr' ? 'tr-TR' : 'en-US';
      }

      if (isAuthTokenClient) {
        return config;
      }

      // Token'ı kontrol et ve ekle
      try {
        const authStateStr = localStorage.getItem('authState');

        if (!authStateStr) {
          return config;
        }

        const authState = JSON.parse(authStateStr);
        const refreshToken = authState.refreshToken;
        const accessToken = authState.accessToken;

        // Token süresi dolmuş mu kontrol et
        if (accessToken && refreshToken && !isRefreshTokenRequest) {
          // ÖNEMLİ: Her istekte token'ın durumunu kontrol et
          const tokenStatus = checkTokenStatus(accessToken);

          // Proaktif yenileme veya süresi dolmuş token durumunda
          if (tokenStatus === 'needs_refresh' || tokenStatus === 'expired') {
            try {
              let newToken: TokenResponse | null = null;

              // Eğer henüz aktif bir yenileme işlemi yoksa, yenile
              if (!pendingRefresh) {
                // Token'ı yenile ve isteğe ekle
                newToken = await refreshTokenAsync(refreshToken);
              } else if (tokenRefreshPromise) {
                // Aktif bir yenileme işlemi var, onu bekle
                newToken = await tokenRefreshPromise;
              }

              // Yeni token varsa, kullanalım
              if (newToken && newToken.access_token) {
                config.headers.Authorization = `Bearer ${newToken.access_token}`;
              } else {
                // Yeni token yoksa mevcut token'ı kullan
                config.headers.Authorization = `Bearer ${accessToken}`;
              }
            } catch (error) {
              // Hata durumunda mevcut token'ı kullan
              config.headers.Authorization = `Bearer ${accessToken}`;
              void error;
            }
          } else {
            // Token geçerli, kullan
            config.headers.Authorization = `Bearer ${accessToken}`;
          }
        } else if (accessToken) {
          // Refresh token yoksa veya refresh token isteği ise, mevcut token'ı kullan
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      } catch (error) {
        void error;
        // Hata durumunda isteği olduğu gibi devam ettir
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (isAuthTokenClient) {
        return Promise.reject(error);
      }

      // 401 hatası durumunda token yenileme
      if (error.response && error.response.status === 401) {
        try {
          const authStateStr = localStorage.getItem('authState');
          if (!authStateStr) {
            // Auth state yoksa login'e yönlendir
            window.location.href = '/login';
            return Promise.reject(error);
          }

          const authState = JSON.parse(authStateStr);
          const refreshToken = authState.refreshToken;

          if (!refreshToken) {
            // Refresh token yoksa login'e yönlendir
            localStorage.removeItem('authState');
            window.location.href = '/login';
            return Promise.reject(error);
          }

          // Token'ı yenile
          const tokenData = await refreshTokenAsync(refreshToken);

          // Orijinal isteği yeni token ile tekrar gönder
          const originalRequest = error.config;
          if (tokenData && tokenData.access_token) {
            originalRequest.headers.Authorization = `Bearer ${tokenData.access_token}`;
            originalRequest.headers['Accept-Language'] =
              localStorage.getItem('i18nextLng') === 'tr' ? 'tr-TR' : 'en-US';

            return axios(originalRequest);
          }
        } catch (refreshError) {
          void refreshError;

          // Tüm hatalarda login'e yönlendir
          localStorage.removeItem('authState');
          localStorage.removeItem('tokenExpiresAt');
          localStorage.removeItem('lastTokenRefresh');
          window.location.href = '/login';

          return errorInterceptor(error);
        }
      }

      return errorInterceptor(error);
    }
  );

  return apiClient;
};

// İlk olarak, localStorage'dan lastTokenRefresh değerini yükle (varsa)
try {
  const lastRefreshStr = localStorage.getItem('lastTokenRefresh');
  if (lastRefreshStr) {
    lastTokenRefreshTime = parseInt(lastRefreshStr, 10);
  }
} catch (e) {
  void e;
}

const gatewayBaseClient = createApiClient(import.meta.env.VITE_GATEWAY_BASE);
const facebookBaseClient = createApiClient(import.meta.env.VITE_FACEBOOK_SERVICE_BASE);

export const userApiClient = createApiClient(import.meta.env.VITE_IDENTITY_URL);
export const loginApiClient = createApiClient(import.meta.env.VITE_AUTH_TOKEN_URL);
export const reportApiClient = createApiClient(import.meta.env.VITE_AUTH_TOKEN_URL);
export const facebookServiceClient = facebookBaseClient;

export const baseClient = gatewayBaseClient;
